import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, fmt, fmtMoney, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { TableCard, StatusBadge, EmptyRow, SkeletonRows, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { downloadProducts } from "@/lib/pdf";
import { Plus, Search, Edit2, Trash2, Package, FileDown } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Insecticide", "Herbicide", "Fungicide", "Fertilizer", "Seed", "Equipment"];
const UNITS = ["kg", "liter", "pack", "unit", "bag"];
const PAGE = 20;

const empty = { name: "", sku: "", category: "Insecticide", brand: "", unit: "kg", purchase_price: 0, selling_price: 0, stock_qty: 0, reorder_level: 10, supplier_name: "", supplier_id: "", expiry_date: "", description: "", active: true };

function statusOf(p) {
  if (p.stock_qty <= 0) return "Out";
  if (p.stock_qty <= p.reorder_level) return "Low";
  return "In Stock";
}

export default function Inventory() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const cat = params.get("category") || "all";
  const status = params.get("status") || "all";
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [page, setPage] = useState(0);
  const [supOpen, setSupOpen] = useState(false);
  const [supName, setSupName] = useState("");
  useEffect(() => { setPage(0); }, [q, cat, status]);

  const { data, isLoading } = useQuery({
    queryKey: ["products", q, cat, status, page],
    queryFn: () => api.get("/products", { params: { q: q || undefined, category: cat, status, skip: page * PAGE, limit: PAGE } })
      .then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] ?? r.data.length) })),
    placeholderData: keepPreviousData,
  });
  const products = data?.items ?? [];
  const total = data?.total ?? 0;

  const { data: suppliers = [] } = useQuery({ queryKey: ["suppliers"], queryFn: () => api.get("/suppliers").then((r) => r.data) });

  const fe = useFormErrors();
  const save = useMutation({
    mutationFn: () =>
      editing
        ? api.put(`/products/${editing.id}`, form)
        // A new product is a catalog entry — it starts at 0 stock. Stock is added later
        // by recording purchases (which also carry reorder level, expiry & date).
        : api.post("/products", { ...form, stock_qty: 0 }),
    onSuccess: () => {
      toast.success(editing ? "Product updated" : "Product added");
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setOpen(false); fe.clear();
    },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const createSupplier = useMutation({
    mutationFn: () => api.post("/suppliers", { name: supName.trim() }),
    onSuccess: (res) => {
      toast.success("Supplier added");
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setForm((f) => ({ ...f, supplier_name: res.data.name, brand: res.data.name, supplier_id: res.data.id }));
      setSupOpen(false); setSupName("");
    },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: () => { toast.success("Product deleted"); qc.invalidateQueries({ queryKey: ["products"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setForm(empty); fe.clear(); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, supplier_id: suppliers.find((s) => s.name === p.supplier_name)?.id || "" }); fe.clear(); setOpen(true); };

  const setParam = (k, v) => { const np = new URLSearchParams(params); if (v) np.set(k, v); else np.delete(k); setParams(np); };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-cap">Catalog</div>
          <h1 className="font-heading text-3xl text-agri-ink mt-1">Inventory & Products</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="export-products-btn"
            onClick={() => downloadProducts({ q, category: cat, status })}
            className={btnOutline + " flex items-center gap-2"}
            title="Download product list as PDF"
          >
            <FileDown size={16} /> PDF
          </button>
          {canEdit && (
            <button data-testid="add-product-btn" onClick={openNew} className={btnAccent + " flex items-center gap-2"}>
              <Plus size={16} /> Add Product
            </button>
          )}
        </div>
      </div>

      <div className="card-soft p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-agri-ink-2" />
          <input data-testid="inventory-search" value={q} onChange={(e) => setParam("q", e.target.value)} placeholder="Search by name, SKU, brand…" className={inputCls + " pl-9"} />
        </div>
        <select data-testid="inventory-category-filter" value={cat} onChange={(e) => setParam("category", e.target.value === "all" ? "" : e.target.value)} className={inputCls + " w-48"}>
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="inventory-status-filter" value={status} onChange={(e) => setParam("status", e.target.value === "all" ? "" : e.target.value)} className={inputCls + " w-40"}>
          <option value="all">Any status</option>
          <option value="in">In stock</option>
          <option value="low">Low</option>
          <option value="out">Out</option>
        </select>
      </div>

      <div className="text-xs text-agri-ink-2">{total} product{total === 1 ? "" : "s"}</div>
      <TableCard testId="products-table">
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Product</th>
            <th className={thCls}>Category</th>
            <th className={thCls}>Brand</th>
            <th className={thNumCls}>Purchase</th>
            <th className={thNumCls}>Selling</th>
            <th className={thNumCls}>Stock</th>
            <th className={thCls}>Status</th>
            <th className={thNumCls}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <SkeletonRows cols={8} />}
          {!isLoading && products.length === 0 && <EmptyRow colSpan={8} icon={Package}>No products found.</EmptyRow>}
          {!isLoading && products.map((p) => (
            <tr key={p.id} className={rowCls}>
              <td className={`${tdCls} rt-primary`}>
                <div className="font-medium text-agri-ink">{p.name}</div>
                <div className="text-xs text-agri-ink-2 font-mono">{p.sku}</div>
              </td>
              <td data-label="Category" className={tdCls}>{p.category}</td>
              <td data-label="Brand" className={`${tdCls} text-agri-ink-2`}>{p.brand}</td>
              <td data-label="Purchase" className={tdNumCls}>{fmtMoney(p.purchase_price)}</td>
              <td data-label="Selling" className={`${tdNumCls} font-semibold`}>{fmtMoney(p.selling_price)}</td>
              <td data-label="Stock" className={tdNumCls}>{fmt(p.stock_qty)} <span className="text-xs text-agri-ink-2">{p.unit}</span></td>
              <td data-label="Status" className={tdCls}><StatusBadge status={statusOf(p)} /></td>
              <td data-label="Actions" className={`${tdCls} text-right whitespace-nowrap`}>
                {canEdit ? (
                  <>
                    <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-black/5 text-agri-ink-2 hover:text-agri-forest"><Edit2 size={15} /></button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => window.confirm("Delete this product?") && del.mutate(p.id)} className="p-1.5 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger ml-1"><Trash2 size={15} /></button>
                  </>
                ) : (
                  <span className="text-xs text-agri-ink-2">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </TableCard>
      <Pagination page={page} pageSize={PAGE} total={total} onPage={setPage} />

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Product" : "Add Product"} size="lg" testId="product-modal">
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4" data-testid="product-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name</label>
              <input data-testid="product-name-input" required value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); fe.clearField("name"); }}
                className={inputCls + (fe.fieldError("name") ? " " + inputErrCls : "")} />
              <FieldError>{fe.fieldError("name")}</FieldError>
            </div>
            <div><label className={labelCls}>SKU / Barcode</label><input value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="Auto-generated" className={inputCls} /></div>
            <div><label className={labelCls}>Category</label>
              <select data-testid="product-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><label className={labelCls}>Brand</label><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Unit</label>
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className={inputCls}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select></div>
            <div><label className={labelCls}>Supplier</label>
              <select
                value={form.supplier_name || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__add__") { setSupName(""); setSupOpen(true); return; }
                  const sup = suppliers.find((s) => s.name === v);
                  setForm({ ...form, supplier_name: v, brand: v, supplier_id: sup?.id || "" });
                }}
                className={inputCls}
              >
                <option value="">— None —</option>
                {form.supplier_name && !suppliers.some((s) => s.name === form.supplier_name) && (
                  <option value={form.supplier_name}>{form.supplier_name} (current)</option>
                )}
                {suppliers.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                <option value="__add__">+ Add new supplier…</option>
              </select></div>
            <div><label className={labelCls}>Purchase price</label><NumberInput value={form.purchase_price} onChange={(n) => setForm({ ...form, purchase_price: n })} /></div>
            <div><label className={labelCls}>Selling price</label><NumberInput data-testid="product-selling-price-input" value={form.selling_price} onChange={(n) => setForm({ ...form, selling_price: n })} /></div>
            {editing && (
              <>
                <div>
                  <label className={labelCls}>Current stock</label>
                  <NumberInput data-testid="product-stock-input" value={form.stock_qty} disabled onChange={() => {}} />
                  <p className="text-[11px] text-agri-ink-2 mt-1">Managed by purchases, sales & Stock Adjustments.</p>
                </div>
                <div><label className={labelCls}>Reorder level</label><NumberInput value={form.reorder_level} onChange={(n) => setForm({ ...form, reorder_level: n })} /></div>
                <div className="flex items-center gap-2 mt-7"><input id="active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /><label htmlFor="active" className="text-sm">Active</label></div>
              </>
            )}
          </div>
          {!editing && <p className="text-[11px] text-agri-ink-2">New products start at 0 stock — add stock by recording a purchase (which also sets reorder level & expiry).</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className={btnOutline}>Cancel</button>
            <button data-testid="product-save-btn" type="submit" disabled={save.isPending} className={btnPrimary}>{editing ? "Update" : "Add"} product</button>
          </div>
        </form>
      </Modal>

      <Modal open={supOpen} onClose={() => setSupOpen(false)} title="New Supplier" size="sm">
        <form onSubmit={(e) => { e.preventDefault(); if (supName.trim()) createSupplier.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Supplier name</label>
            <input autoFocus required value={supName} onChange={(e) => setSupName(e.target.value)} className={inputCls} placeholder="e.g. Bayer Crop Science" />
          </div>
          <p className="text-xs text-agri-ink-2">Creates the supplier and selects it. You can add contact details later in Suppliers.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setSupOpen(false)} className={btnOutline}>Cancel</button>
            <button className={btnPrimary} disabled={createSupplier.isPending}>Add supplier</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
